// src/components/common/SessionTimeoutHandler.jsx
// ROBUST IMPLEMENTATION: Activity monitoring + Token refresh with fail-safes
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/contexts/AuthContext';
import authService from '@/services/authService';

const SessionTimeoutHandler = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  // Timer refs
  const activityTimeoutRef = useRef(null);
  const tokenRefreshRef = useRef(null);
  const activityDebounceRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Handler function refs (avoid stale closures)
  const handleTimeoutRef = useRef(null);
  const handleUserActivityRef = useRef(null);
  const scheduleTokenRefreshRef = useRef(null);
  
  // Configuration
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000; // 25 minutes
  const ACTIVITY_DEBOUNCE = 2000; // 2 seconds - debounce activity events

  // Timeout handler - logs user out
  useEffect(() => {
    handleTimeoutRef.current = () => {
      console.log('🚨 30-MINUTE INACTIVITY TIMEOUT TRIGGERED');
      console.log('🚨 SESSION TIMEOUT: Logging out user due to inactivity');
      console.log('🚨 Timeout occurred at:', new Date().toISOString());
      
      // Clear all timers
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }
      if (tokenRefreshRef.current) {
        clearTimeout(tokenRefreshRef.current);
        tokenRefreshRef.current = null;
      }
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
        activityDebounceRef.current = null;
      }
      
      // Log the user out
      logout();
      
      // Clear storage
      localStorage.removeItem('hfrconnect_registration');
      sessionStorage.removeItem('hfrconnect_registration_session');
      sessionStorage.removeItem('hfrconnect_registration_timestamp');
      sessionStorage.removeItem('hfrconnect_last_activity');
      
      // Clear explore opportunities state on timeout
      sessionStorage.removeItem('exploreOpportunities_viewMode');
      sessionStorage.removeItem('exploreOpportunities_filters');
      sessionStorage.removeItem('exploreOpportunities_page');
      sessionStorage.removeItem('exploreOpportunities_search');
      sessionStorage.removeItem('exploreOpportunities_gridView');
      sessionStorage.removeItem('exploreOpportunities_metricsExpanded');
      sessionStorage.removeItem('exploreOpportunities_scrollPosition');
      
      // Redirect to login with timeout message
      navigate('/login', { state: { timeout: true } });
    };
  }, [logout, navigate]);

  // Activity handler - resets inactivity timer (DEBOUNCED)
  useEffect(() => {
    handleUserActivityRef.current = () => {
      const now = Date.now();
      const timeSinceLastActivity = (now - lastActivityRef.current) / 1000;
      
      // Log activity if significant time has passed (reduce noise)
      if (timeSinceLastActivity > 60) {
        console.log(`⚡ User activity detected (${Math.floor(timeSinceLastActivity)}s since last activity)`);
      }
      
      // Update last activity time
      lastActivityRef.current = now;
      sessionStorage.setItem('hfrconnect_last_activity', now.toString());
      sessionStorage.setItem('hfrconnect_registration_timestamp', now.toString());
      
      // Clear existing activity timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      
      // Start new 30-minute inactivity timer
      activityTimeoutRef.current = setTimeout(() => {
        console.log('🚨 30-MINUTE INACTIVITY TIMEOUT TRIGGERED');
        if (handleTimeoutRef.current) {
          handleTimeoutRef.current();
        }
      }, INACTIVITY_TIMEOUT);
      
      if (timeSinceLastActivity > 60) {
        console.log('⏰ Activity timer RESET - 30-minute countdown restarted');
      }
    };
  }, [INACTIVITY_TIMEOUT]);

  // Token refresh handler - keeps tokens valid
  useEffect(() => {
    scheduleTokenRefreshRef.current = (retryDelayMs = TOKEN_REFRESH_INTERVAL) => {
      if (tokenRefreshRef.current) {
        clearTimeout(tokenRefreshRef.current);
      }
      
      const performRefresh = async () => {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          const userId = localStorage.getItem('userId');
          
          if (!refreshToken) {
            console.log('⚠️ No refresh token available for proactive refresh');
            return;
          }
          
          if (!userId) {
            console.warn('⚠️ No userId available for proactive refresh - this may cause issues');
          }
          
          console.log('🔄 Performing proactive token refresh...');
          
          // Use the authService refresh method
          await authService.refreshToken();
          
          console.log('✅ Token refreshed proactively at:', new Date().toISOString());
          
          // Schedule next refresh at the normal interval
          if (scheduleTokenRefreshRef.current) {
            scheduleTokenRefreshRef.current(TOKEN_REFRESH_INTERVAL);
          }
        } catch (error) {
          console.error('❌ Proactive token refresh failed:', error);
          // Retry with exponential backoff (cap at normal interval) so one transient
          // failure doesn't permanently kill the refresh timer before token expiry.
          const backoff = Math.min(retryDelayMs * 2, TOKEN_REFRESH_INTERVAL);
          console.warn(`⚠️ Scheduling retry token refresh in ${Math.round(backoff / 1000)}s`);
          if (scheduleTokenRefreshRef.current) {
            scheduleTokenRefreshRef.current(backoff);
          }
        }
      };
      
      tokenRefreshRef.current = setTimeout(performRefresh, retryDelayMs);
      
      const nextRefreshTime = new Date(Date.now() + retryDelayMs);
      console.log(`⏰ Next token refresh scheduled for: ${nextRefreshTime.toLocaleTimeString()}`);
    };
  }, [TOKEN_REFRESH_INTERVAL]);

  // Debounced activity handler - prevents excessive timer resets
  const handleUserActivity = useCallback(() => {
    // Clear existing debounce timer
    if (activityDebounceRef.current) {
      clearTimeout(activityDebounceRef.current);
    }
    
    // Set new debounce timer
    activityDebounceRef.current = setTimeout(() => {
      if (handleUserActivityRef.current) {
        handleUserActivityRef.current();
      }
    }, ACTIVITY_DEBOUNCE);
  }, [ACTIVITY_DEBOUNCE]);

  // Main effect - runs ONCE on mount, cleans up on unmount
  useEffect(() => {
    console.log('🚀 SessionTimeoutHandler: Initializing ROBUST dual-timer system');
    console.log(`⏰ Configuration: Inactivity timeout = ${INACTIVITY_TIMEOUT / 60000} minutes`);
    console.log(`⏰ Configuration: Token refresh = ${TOKEN_REFRESH_INTERVAL / 60000} minutes`);
    console.log(`⏰ Configuration: Activity debounce = ${ACTIVITY_DEBOUNCE / 1000} seconds`);
    
    // Initialize activity timer (immediate)
    if (handleUserActivityRef.current) {
      handleUserActivityRef.current();
      console.log('✅ Activity timer started');
    }
    
    // Initialize token refresh timer
    if (scheduleTokenRefreshRef.current) {
      scheduleTokenRefreshRef.current();
      console.log('✅ Token refresh timer started');
    }
    
    // Add event listeners for user activity (passive for performance)
    console.log('📡 Attaching activity event listeners (mousemove, keypress, click, scroll, touchstart)');
    window.addEventListener('mousemove', handleUserActivity, { passive: true });
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('click', handleUserActivity, { passive: true });
    window.addEventListener('scroll', handleUserActivity, { passive: true });
    window.addEventListener('touchstart', handleUserActivity, { passive: true });
    
    // Cleanup function - runs on unmount
    return () => {
      console.log('🧹 SessionTimeoutHandler: Cleaning up dual-timer system');
      
      // Clear all timers
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
        console.log('🧹 Activity timer cleared');
      }
      if (tokenRefreshRef.current) {
        clearTimeout(tokenRefreshRef.current);
        tokenRefreshRef.current = null;
        console.log('🧹 Token refresh timer cleared');
      }
      if (activityDebounceRef.current) {
        clearTimeout(activityDebounceRef.current);
        activityDebounceRef.current = null;
        console.log('🧹 Activity debounce timer cleared');
      }
      
      // Remove event listeners
      console.log('🧹 Removing activity event listeners');
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // EMPTY ARRAY - only runs once on mount/unmount

  // This component doesn't render anything
  return null;
};

export default SessionTimeoutHandler;
